import jsPDF from 'jspdf';
import 'jspdf-autotable';

class PDFExportService {
  constructor() {
    this.doc = null;
  }

  /**
   * Export study plan to PDF
   */
  async exportStudyPlan(studyPlan, userRoutine) {
    try {
      console.log('🔄 PDFExportService: Generating PDF...', studyPlan);
      
      this.doc = new jsPDF();
      
      // Add header
      this.addHeader();
      
      // Add study plan overview
      this.addOverview(studyPlan);
      
      // Add daily schedule
      this.addDailySchedule(studyPlan);
      
      // Add study goals
      this.addStudyGoals(studyPlan);
      
      // Add user routine
      this.addUserRoutine(userRoutine);
      
      // Add study tips
      this.addStudyTips();
      
      // Save the PDF
      const fileName = `study-plan-${new Date().toISOString().split('T')[0]}.pdf`;
      this.doc.save(fileName);
      
      console.log('✅ PDFExportService: PDF generated successfully');
      return fileName;
    } catch (error) {
      console.error('❌ PDFExportService: Error generating PDF:', error);
      throw error;
    }
  }

  addHeader() {
    this.doc.setFontSize(24);
    this.doc.setTextColor(59, 130, 246); // Blue color
    this.doc.text('AI Study Plan', 20, 30);
    
    this.doc.setFontSize(12);
    this.doc.setTextColor(107, 114, 128); // Gray color
    this.doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 40);
    
    this.doc.setDrawColor(59, 130, 246);
    this.doc.setLineWidth(0.5);
    this.doc.line(20, 45, 190, 45);
  }

  addOverview(studyPlan) {
    this.doc.setFontSize(16);
    this.doc.setTextColor(31, 41, 55); // Dark gray
    this.doc.text('Study Plan Overview', 20, 65);
    
    this.doc.setFontSize(10);
    this.doc.setTextColor(75, 85, 99); // Medium gray
    
    const overviewData = [
      ['Study Period', studyPlan.duration || '4 weeks'],
      ['Total Study Hours', studyPlan.totalHours || '120 hours'],
      ['Daily Study Hours', studyPlan.dailyHours || '4 hours'],
      ['Number of Subjects', studyPlan.subjects?.length || 5],
      ['Start Date', studyPlan.startDate || 'TBD'],
      ['End Date', studyPlan.endDate || 'TBD']
    ];
    
    this.doc.autoTable({
      startY: 75,
      head: [['Metric', 'Value']],
      body: overviewData,
      theme: 'grid',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 10
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      margin: { left: 20, right: 20 }
    });
  }

  addDailySchedule(studyPlan) {
    const currentY = this.doc.lastAutoTable.finalY + 20;
    
    this.doc.setFontSize(16);
    this.doc.setTextColor(31, 41, 55);
    this.doc.text('Weekly Study Schedule', 20, currentY);
    
    if (studyPlan.schedule && studyPlan.schedule.length > 0) {
      const scheduleData = studyPlan.schedule.map(timeSlot => {
        const row = [timeSlot.time];
        timeSlot.days.forEach(day => {
          row.push(day.subject || day.activity || '');
        });
        return row;
      });
      
      this.doc.autoTable({
        startY: currentY + 10,
        head: [['Time', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']],
        body: scheduleData,
        theme: 'grid',
        headStyles: {
          fillColor: [34, 197, 94], // Green color
          textColor: 255,
          fontSize: 9
        },
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        margin: { left: 20, right: 20 }
      });
    } else {
      this.doc.setFontSize(10);
      this.doc.setTextColor(107, 114, 128);
      this.doc.text('Your personalized study schedule will be generated here.', 20, currentY + 15);
    }
  }

  addStudyGoals(studyPlan) {
    const currentY = this.doc.lastAutoTable.finalY + 20;
    
    this.doc.setFontSize(16);
    this.doc.setTextColor(31, 41, 55);
    this.doc.text('Study Goals & Milestones', 20, currentY);
    
    if (studyPlan.goals && studyPlan.goals.length > 0) {
      const goalsData = studyPlan.goals.map(goal => [
        goal.completed ? '✓' : '○',
        goal.title,
        goal.description,
        goal.deadline || 'TBD'
      ]);
      
      this.doc.autoTable({
        startY: currentY + 10,
        head: [['Status', 'Goal', 'Description', 'Deadline']],
        body: goalsData,
        theme: 'grid',
        headStyles: {
          fillColor: [168, 85, 247], // Purple color
          textColor: 255,
          fontSize: 9
        },
        styles: {
          fontSize: 8,
          cellPadding: 3
        },
        margin: { left: 20, right: 20 }
      });
    } else {
      this.doc.setFontSize(10);
      this.doc.setTextColor(107, 114, 128);
      this.doc.text('Study goals will be set based on your exam schedule.', 20, currentY + 15);
    }
  }

  addUserRoutine(userRoutine) {
    const currentY = this.doc.lastAutoTable.finalY + 20;
    
    this.doc.setFontSize(16);
    this.doc.setTextColor(31, 41, 55);
    this.doc.text('Your Daily Routine', 20, currentY);
    
    const routineData = [
      ['Wake Up Time', userRoutine.wakeUpTime],
      ['Sleep Time', userRoutine.sleepTime],
      ['School Hours', `${userRoutine.schoolHours.start} - ${userRoutine.schoolHours.end}`],
      ['Mobile Usage', `${userRoutine.mobileUsage} hours/day`],
      ['TV Time', `${userRoutine.tvTime} hours/day`],
      ['Family Time', `${userRoutine.familyTime} hours/day`],
      ['Gaming Time', `${userRoutine.gamingTime} hours/day`],
      ['Study Breaks', `${userRoutine.studyBreaks} minutes`],
      ['Preferred Study Times', userRoutine.preferredStudyTimes.join(', ')],
      ['Subjects', userRoutine.subjects.join(', ')]
    ];
    
    this.doc.autoTable({
      startY: currentY + 10,
      head: [['Activity', 'Time']],
      body: routineData,
      theme: 'grid',
      headStyles: {
        fillColor: [239, 68, 68], // Red color
        textColor: 255,
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      margin: { left: 20, right: 20 }
    });
  }

  addStudyTips() {
    const currentY = this.doc.lastAutoTable.finalY + 20;
    
    this.doc.setFontSize(16);
    this.doc.setTextColor(31, 41, 55);
    this.doc.text('Study Tips & Strategies', 20, currentY);
    
    const tips = [
      'Create a dedicated study space free from distractions',
      'Use the Pomodoro Technique: 25 minutes study, 5 minutes break',
      'Review your notes within 24 hours of learning',
      'Practice active recall instead of passive reading',
      'Teach concepts to others to reinforce your understanding',
      'Get adequate sleep (7-9 hours) for better memory retention',
      'Stay hydrated and take regular breaks',
      'Use mnemonic devices for memorization',
      'Set specific, achievable daily goals',
      'Track your progress and celebrate small wins'
    ];
    
    this.doc.setFontSize(10);
    this.doc.setTextColor(75, 85, 99);
    
    tips.forEach((tip, index) => {
      const y = currentY + 15 + (index * 8);
      if (y < 280) { // Check if we have space on the page
        this.doc.text(`• ${tip}`, 20, y);
      }
    });
    
    // Add footer
    this.doc.setFontSize(8);
    this.doc.setTextColor(107, 114, 128);
    this.doc.text('Generated by AI Study Planner - Your personalized academic companion', 20, 290);
  }

  /**
   * Export study plan as calendar events (ICS format)
   */
  generateICSFile(studyPlan) {
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//AI Study Planner//Study Plan//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH'
    ];

    if (studyPlan.schedule) {
      studyPlan.schedule.forEach((timeSlot, index) => {
        timeSlot.days.forEach((day, dayIndex) => {
          if (day.subject) {
            const event = [
              'BEGIN:VEVENT',
              `UID:study-${index}-${dayIndex}-${Date.now()}`,
              `SUMMARY:${day.subject} Study Session`,
              `DESCRIPTION:${day.activity || 'Study session'}`,
              `DTSTART:${this.formatDateTime(timeSlot.time)}`,
              `DTEND:${this.formatDateTime(this.addHours(timeSlot.time, 1))}`,
              'PRIORITY:1',
              'END:VEVENT'
            ];
            icsContent.push(...event);
          }
        });
      });
    }

    icsContent.push('END:VCALENDAR');

    const blob = new Blob([icsContent.join('\r\n')], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `study-plan-${new Date().toISOString().split('T')[0]}.ics`;
    link.click();
    
    URL.revokeObjectURL(url);
  }

  formatDateTime(time) {
    const today = new Date();
    const [hours, minutes] = time.split(':');
    today.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return today.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  }

  addHours(time, hours) {
    const [h, m] = time.split(':').map(Number);
    const newHours = (h + hours) % 24;
    return `${newHours.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
}

export default new PDFExportService(); 